import React, { useState, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  Home,
  Droplets,
  Bug,
  FileText,
  Shield,
  Calendar,
  Tractor,
  Map,
  Download,
  GitBranch,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  WorkspacePageLayout,
  EventFeed,
  MockDataLabel,
  ContextPanel,
  LegalQuotePanel,
  EntityCard,
  ReasoningPanel,
  AuditPackPreview,
  CalculationDetailsPanel,
  TwinEventCard,
  EventFilterBar,
  type EventData,
  type LenseItem,
  type MockControlEvent,
  type MockEntity,
  type LegalReference,
  type AuditPackGroup,
  type CalculationDetails,
  type EventSeverity,
  type EventStatus,
  type EventCategory,
} from '@hofsync/design-system';

import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { useTwinEvents, useEventActions } from '../hooks/useTwin';
import { useTwinSSE } from '../hooks/useTwinSSE';

// Import mock data
import {
  ALL_CONTROL_EVENTS,
  getEventsByCategory,
} from '../mocks/betrieb-mueller/events';
import { getAllLegalRefs } from '../mocks/legal';
import { getEntityById } from '../mocks/betrieb-mueller/entities';
import { MOCK_EVENTS } from '../mocks/betrieb-mueller/twin';

// Panel types for context panel
type PanelType = 'legal' | 'entity' | 'document' | 'audit-pack' | 'calculation' | null;

interface PanelData {
  legalRef?: LegalReference;
  entity?: MockEntity;
  documentId?: string;
  auditPackGroups?: AuditPackGroup[];
  controlType?: string;
  calculationDetails?: CalculationDetails;
}

// Workspace categories - translated using i18next
const getWorkspaceCategories = (t: (key: string) => string): LenseItem[] => {
  return [
    { id: 'all', name: t('workspace.categories.all'), icon: <Home /> },
    {
      id: 'kontrollen',
      name: t('workspace.categories.kontrollen'),
      icon: <Shield />,
      children: [
        { id: 'duev', name: t('workspace.categories.duev') },
        { id: 'cross-compliance', name: t('workspace.categories.crossCompliance') },
        { id: 'pflanzenschutz', name: t('workspace.categories.pflanzenschutz') },
        { id: 'tierhaltung', name: t('workspace.categories.tierhaltung') },
      ],
    },
    {
      id: 'dokumente',
      name: t('workspace.categories.dokumente'),
      icon: <Calendar />,
      children: [
        { id: 'tuev', name: t('workspace.categories.tuev') },
        { id: 'sachkunde', name: t('workspace.categories.sachkunde') },
        { id: 'bodenproben', name: t('workspace.categories.bodenproben') },
        { id: 'unterweisungen', name: t('workspace.categories.unterweisungen') },
      ],
    },
    { id: 'manure', name: t('workspace.categories.manure'), icon: <Droplets /> },
    { id: 'plant-protection', name: t('workspace.categories.plantProtection'), icon: <Bug /> },
    { id: 'equipment', name: t('workspace.categories.equipment'), icon: <Tractor /> },
    { id: 'fields', name: t('workspace.categories.fields'), icon: <Map /> },
    { id: 'subsidies', name: t('workspace.categories.subsidies'), icon: <FileText /> },
    {
      id: 'twin',
      name: 'Twin-Ereignisse',
      icon: <GitBranch />,
      children: [
        { id: 'twin-all', name: 'Alle' },
        { id: 'twin-compliance', name: 'Compliance' },
        { id: 'twin-lifecycle', name: 'Lebenszyklus' },
        { id: 'twin-general', name: 'Allgemein' },
      ],
    },
  ];
};

/**
 * Build event details with reasoning panel.
 *
 * CARD STRUCTURE (per spec):
 * - Summary (description) - rendered by EventTile
 * - Affected Objects (affectedEntities) - rendered by EventTile
 * - Primary Actions (actions) - rendered by EventTile
 * - Why This Matters (reasoning) - rendered here via ReasoningPanel
 * - Calculation trigger - "Wie wurde das berechnet?" opens side panel
 * - Audit Actions - ONLY if event has isAuditPack action
 *
 * ANTI-PATTERNS AVOIDED:
 * - No duplicate actions (actions are in EventTile, not here)
 * - No "Prüfpaket erstellen" for events without audit pack action
 */
function buildEventDetails(
  event: MockControlEvent,
  legalRefMap: Record<string, LegalReference>,
  onLegalClick: (refId: string) => void,
  onEntityClick: (entityId: string) => void,
  onCalculationClick: () => void,
  onAuditPackClick: () => void
): ReactNode {
  if (!event.reasoning) {
    return null;
  }

  // Only show audit pack button if event explicitly has an audit pack action
  const hasAuditPackAction = event.actions?.some((a) => a.isAuditPack);

  return (
    <div className="event-tile__details--reasoning">
      <ReasoningPanel
        reasoning={event.reasoning}
        legalRefMap={legalRefMap}
        onLegalClick={onLegalClick}
        onEntityClick={onEntityClick}
        onCalculationClick={onCalculationClick}
      />
      {hasAuditPackAction && (
        <div className="event-tile__actions event-tile__actions--audit">
          <button
            className="event-tile__action-button event-tile__action-button--primary"
            onClick={onAuditPackClick}
          >
            <Download size={16} />
            Prüfpaket erstellen
          </button>
        </div>
      )}
    </div>
  );
}

// Convert MockControlEvent to EventData with reasoning details
function toEventData(
  event: MockControlEvent,
  legalRefMap: Record<string, LegalReference>,
  onLegalClick: (refId: string) => void,
  onEntityClick: (entityId: string) => void,
  onCalculationClick: (eventId: string) => void,
  onAuditPackClick: (eventId: string) => void,
  onActionClick: (actionId: string) => void,
  onNavigate: (href: string) => void,
): EventData {
  return {
    id: event.id,
    type: event.type,
    priority: event.priority,
    title: event.title,
    subtitle: event.subtitle,
    description: event.description,
    timestamp: event.timestamp,
    relevantDate: event.relevantDate,
    relatedEntity: event.relatedEntity,
    // Aggregate event support
    isAggregate: event.isAggregate,
    hideAggregateBadge: event.hideAggregateBadge,
    affectedEntities: event.affectedEntities,
    domainLink: event.domainLink,
    actions: event.actions,
    onEntityClick,
    onDomainLinkClick: event.domainLink ? () => onNavigate(event.domainLink!.href) : undefined,
    onActionClick,
    // Status indicators
    controlReadiness: event.controlReadiness,
    inspectionLikelihood: event.inspectionLikelihood,
    details: buildEventDetails(
      event,
      legalRefMap,
      onLegalClick,
      onEntityClick,
      () => onCalculationClick(event.id),
      () => onAuditPackClick(event.id)
    ),
  };
}

// Build audit pack groups for DÜV control
function buildDuevAuditPackGroups(): AuditPackGroup[] {
  return [
    {
      name: 'Düngebedarfsermittlung',
      legalBasis: 'DüV § 3',
      documents: [
        {
          id: 'doc-dbe-2025',
          name: 'Düngebedarfsermittlung 2025',
          type: 'nachweis',
          date: new Date('2025-02-15'),
          source: 'HofSync',
          status: 'present',
          category: 'dbe',
        },
      ],
    },
    {
      name: 'Bodenproben',
      legalBasis: 'DüV § 4',
      documents: [
        {
          id: 'doc-bp-grosses-feld',
          name: 'Bodenprobe - Großes Feld',
          type: 'bodenprobe',
          date: new Date('2019-03-14'),
          source: 'LUFA Nord-West',
          status: 'expired',
          category: 'bodenproben',
        },
        {
          id: 'doc-bp-waldacker',
          name: 'Bodenprobe - Waldacker',
          type: 'bodenprobe',
          date: new Date('2023-04-20'),
          source: 'LUFA Nord-West',
          status: 'present',
          category: 'bodenproben',
        },
      ],
    },
    {
      name: 'Aufzeichnungen',
      legalBasis: 'DüV § 10',
      documents: [
        {
          id: 'doc-nv-2025',
          name: 'Nährstoffvergleich 2025',
          type: 'nachweis',
          source: '',
          status: 'missing',
          category: 'aufzeichnungen',
        },
        {
          id: 'doc-schlagkartei-2024',
          name: 'Schlagkartei 2024',
          type: 'nachweis',
          date: new Date('2024-12-31'),
          source: 'HofSync',
          status: 'present',
          category: 'aufzeichnungen',
        },
      ],
    },
    {
      name: 'Stoffstrombilanz',
      legalBasis: 'StoffBilV § 6',
      documents: [
        {
          id: 'doc-ssb-2024',
          name: 'Stoffstrombilanz 2024',
          type: 'nachweis',
          date: new Date('2025-03-28'),
          source: 'HofSync',
          status: 'present',
          category: 'stoffstrombilanz',
        },
      ],
    },
  ];
}

const WorkspacePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasFlag } = useFeatureFlags();
  const useLiveData = hasFlag('twin.events');

  // SSE for live updates
  useTwinSSE({ enabled: useLiveData });

  const [selectedCategory, setSelectedCategory] = useState(useLiveData ? 'twin' : 'all');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);

  // Context panel state
  const [panelType, setPanelType] = useState<PanelType>(null);
  const [panelData, setPanelData] = useState<PanelData>({});

  // Twin event filter state
  const [twinSeverity, setTwinSeverity] = useState<EventSeverity | null>(null);
  const [twinStatus, setTwinStatus] = useState<EventStatus | null>(null);
  const [twinCategory, setTwinCategory] = useState<EventCategory | null>(null);
  const [twinEventStates, setTwinEventStates] = useState<Record<string, string>>({});

  // Live twin data hooks
  const { data: liveEventsData } = useTwinEvents({ limit: 100 });
  const { acknowledge, dismiss, resolve } = useEventActions();

  // Resolve twin events data source — live mode: never fall back to mock data
  const twinEvents = useLiveData
    ? (liveEventsData?.events ?? [])
    : MOCK_EVENTS;

  // Get all legal references
  const legalRefMap = useMemo(() => getAllLegalRefs(), []);

  // Get workspace categories — live mode: only show twin categories
  const workspaceCategories = useMemo(() => {
    const all = getWorkspaceCategories(t);
    if (useLiveData) {
      return all.filter((c) => c.id === 'twin');
    }
    return all;
  }, [t, useLiveData]);

  // Translated labels
  const labels = useMemo(() => ({
    demoData: t('workspace.demoData'),
    workspace: t('workspace.title'),
    noEvents: t('workspace.noEvents'),
    now: t('workspace.timeGroups.now'),
    nextMonth: t('workspace.timeGroups.nextMonth'),
    thisSeason: t('workspace.timeGroups.thisSeason'),
    earlier: t('workspace.timeGroups.earlier'),
    auditPack: t('workspace.auditPack'),
    documentPreview: t('workspace.documentPreview'),
  }), [t]);

  const handleCategorySelect = (categoryId: string, subcategoryId: string | null) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory(subcategoryId);
  };

  // Panel handlers
  const handleLegalClick = useCallback(
    (refId: string) => {
      const ref = legalRefMap[refId];
      if (ref) {
        setPanelData({ legalRef: ref });
        setPanelType('legal');
      }
    },
    [legalRefMap]
  );

  const handleEntityClick = useCallback((entityId: string) => {
    const entity = getEntityById(entityId);
    if (entity) {
      setPanelData({ entity });
      setPanelType('entity');
    }
  }, []);

  const handleDocumentClick = useCallback((documentId: string) => {
    setPanelData({ documentId });
    setPanelType('document');
  }, []);

  const handleAuditPackClick = useCallback((_eventId: string) => {
    // Build audit pack groups based on the event
    // TODO: Use eventId to determine which audit pack to build
    const groups = buildDuevAuditPackGroups();
    setPanelData({
      auditPackGroups: groups,
      controlType: 'DÜV-Kontrolle',
    });
    setPanelType('audit-pack');
  }, []);

  const handleCalculationClick = useCallback((eventId: string) => {
    // Find the event and get its calculation details
    const event = ALL_CONTROL_EVENTS.find((e) => e.id === eventId);
    if (event?.reasoning?.calculationDetails) {
      setPanelData({
        calculationDetails: event.reasoning.calculationDetails,
      });
      setPanelType('calculation');
    } else {
      // Generate default calculation details from factors if no explicit details
      const defaultDetails: CalculationDetails = {
        header: {
          title: `Berechnungsgrundlage – ${event?.title || 'Ereignis'}`,
          statusBadge: 'Automatisch geprüft',
          timestamp: 'Stand: heute',
        },
        facts: event?.reasoning?.factors?.map((f) => ({
          label: f.label,
          value: f.value,
          source: f.source,
        })) || [],
        logic: {
          ruleExpression: event?.reasoning?.calculation?.formula || 'Regelbasierte Prüfung',
          steps: [
            { description: 'Daten aus digitalem Zwilling geladen' },
            { description: 'Regel gegen Daten geprüft' },
            { description: event?.reasoning?.calculation?.result || 'Ergebnis ermittelt', isDecisive: true },
          ],
        },
        result: {
          status: event?.reasoning?.calculation?.result || 'Geprüft',
          severity: event?.priority === 'urgent' ? 'critical' : event?.priority === 'warning' ? 'warning' : 'success',
        },
      };

      // Add legal basis if available
      if (event?.reasoning?.legalBasis?.length) {
        const firstLegal = event.reasoning.legalBasis[0];
        defaultDetails.legalBasis = {
          regulationName: firstLegal.lawFullName,
          paragraph: firstLegal.shortCitation,
          summary: firstLegal.relevanceExplanation || firstLegal.title,
          sourceUrl: firstLegal.sourceUrl,
        };
      }

      setPanelData({ calculationDetails: defaultDetails });
      setPanelType('calculation');
    }
  }, []);

  const handleActionClick = useCallback((actionId: string) => {
    // TODO: Implement action handlers for specific action IDs
    console.log('Action clicked:', actionId);
    // For now, just log the action
  }, []);

  // Twin event handlers
  const handleTwinAcknowledge = useCallback((eventId: string) => {
    if (useLiveData) {
      acknowledge.mutate(eventId);
    } else {
      setTwinEventStates((prev) => ({ ...prev, [eventId]: 'acknowledged' }));
    }
  }, [useLiveData, acknowledge]);
  const handleTwinDismiss = useCallback((eventId: string) => {
    if (useLiveData) {
      dismiss.mutate(eventId);
    } else {
      setTwinEventStates((prev) => ({ ...prev, [eventId]: 'dismissed' }));
    }
  }, [useLiveData, dismiss]);
  const handleTwinResolve = useCallback((eventId: string) => {
    if (useLiveData) {
      resolve.mutate(eventId);
    } else {
      setTwinEventStates((prev) => ({ ...prev, [eventId]: 'resolved' }));
    }
  }, [useLiveData, resolve]);

  // Filter twin events
  const isTwinView = selectedCategory === 'twin';
  const filteredTwinEvents = useMemo(() => {
    if (!isTwinView) return [];
    const effectiveSub = selectedSubcategory || 'twin-all';
    let events = twinEvents.filter(
      (e) =>
        e.status !== 'resolved' &&
        (useLiveData || !twinEventStates[e.event_id]),
    );
    if (effectiveSub === 'twin-compliance') events = events.filter((e) => e.category === 'compliance');
    else if (effectiveSub === 'twin-lifecycle') events = events.filter((e) => e.category === 'lifecycle');
    else if (effectiveSub === 'twin-general') events = events.filter((e) => e.category === 'general' || e.category === 'reconciliation');

    if (twinSeverity) events = events.filter((e) => e.severity === twinSeverity);
    if (twinStatus) events = events.filter((e) => e.status === twinStatus);
    if (twinCategory) events = events.filter((e) => e.category === twinCategory);
    return events;
  }, [isTwinView, selectedSubcategory, twinSeverity, twinStatus, twinCategory, twinEventStates, twinEvents, useLiveData]);

  const closePanel = useCallback(() => {
    setPanelType(null);
    setPanelData({});
  }, []);

  // Filter events based on selected category/subcategory
  const filteredEvents = useMemo(() => {
    let events: MockControlEvent[];

    // Handle subcategory filtering
    const effectiveCategory = selectedSubcategory || selectedCategory;

    if (effectiveCategory === 'all') {
      events = ALL_CONTROL_EVENTS;
    } else if (
      effectiveCategory === 'kontrollen' ||
      effectiveCategory === 'dokumente'
    ) {
      // Parent category - show all children
      const childCategories =
        effectiveCategory === 'kontrollen'
          ? ['duev', 'cross-compliance', 'pflanzenschutz', 'tierhaltung']
          : ['tuev', 'sachkunde', 'bodenproben', 'unterweisungen'];

      events = ALL_CONTROL_EVENTS.filter((event) =>
        event.categories?.some((cat) => childCategories.includes(cat))
      );
    } else {
      // Specific category
      events = getEventsByCategory(effectiveCategory as any);
    }

    return events;
  }, [selectedCategory, selectedSubcategory]);

  // Convert to EventData with drill-down handlers
  const eventDataList = useMemo(() => {
    return filteredEvents.map((event) =>
      toEventData(
        event,
        legalRefMap,
        handleLegalClick,
        handleEntityClick,
        handleCalculationClick,
        handleAuditPackClick,
        handleActionClick,
        navigate,
      )
    );
  }, [filteredEvents, legalRefMap, handleLegalClick, handleEntityClick, handleCalculationClick, handleAuditPackClick, handleActionClick, navigate]);

  // Check if audit pack is complete
  const auditPackComplete = useMemo(() => {
    if (!panelData.auditPackGroups) return false;
    return panelData.auditPackGroups.every((group) =>
      group.documents.every((doc) => doc.status === 'present')
    );
  }, [panelData.auditPackGroups]);

  return (
    <>
      {!useLiveData && (
        <MockDataLabel
          text={labels.demoData}
          position="top-right"
          color="blue"
        />
      )}
      <WorkspacePageLayout
        categories={workspaceCategories}
        selectedCategory={selectedCategory}
        selectedSubcategory={selectedSubcategory}
        onCategorySelect={handleCategorySelect}
        rootLabel={labels.workspace}
      >
        {isTwinView ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <EventFilterBar
              selectedSeverity={twinSeverity}
              selectedStatus={twinStatus}
              selectedCategory={twinCategory}
              onSeverityChange={setTwinSeverity}
              onStatusChange={setTwinStatus}
              onCategoryChange={setTwinCategory}
            />
            {useLiveData && !liveEventsData ? (
              <p style={{ color: '#6b7280', padding: '16px 0' }}>
                Twin-Ereignisse werden geladen...
              </p>
            ) : filteredTwinEvents.length === 0 ? (
              <p style={{ color: '#6b7280', fontStyle: 'italic', padding: '16px 0' }}>
                Keine Twin-Ereignisse gefunden.
              </p>
            ) : (
              filteredTwinEvents.map((event) => (
                <TwinEventCard
                  key={event.event_id}
                  event={event}
                  onAcknowledge={handleTwinAcknowledge}
                  onDismiss={handleTwinDismiss}
                  onResolve={handleTwinResolve}
                />
              ))
            )}
          </div>
        ) : useLiveData ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p style={{ color: '#6b7280', fontWeight: 500 }}>Demnächst verfügbar</p>
          </div>
        ) : (
          <EventFeed
            events={eventDataList}
            emptyMessage={labels.noEvents}
            groupLabels={{
              now: labels.now,
              'next-month': labels.nextMonth,
              'this-season': labels.thisSeason,
              earlier: labels.earlier,
            }}
          />
        )}
      </WorkspacePageLayout>

      {/* Legal Quote Panel */}
      <LegalQuotePanel
        isOpen={panelType === 'legal'}
        onClose={closePanel}
        legalRef={panelData.legalRef || null}
      />

      {/* Entity Panel */}
      <ContextPanel
        isOpen={panelType === 'entity'}
        onClose={closePanel}
        title={panelData.entity?.name || 'Entity'}
        subtitle={panelData.entity?.type || ''}
        width="medium"
      >
        {panelData.entity && (
          <EntityCard
            entity={panelData.entity}
            variant="panel"
            onViewFull={(id) => console.log('View full entity:', id)}
            onDocumentClick={handleDocumentClick}
            onUploadDocument={(id) => console.log('Upload document for entity:', id)}
          />
        )}
      </ContextPanel>

      {/* Audit Pack Panel */}
      <ContextPanel
        isOpen={panelType === 'audit-pack'}
        onClose={closePanel}
        title="Prüfpaket"
        subtitle={panelData.controlType}
        width="medium"
      >
        {panelData.auditPackGroups && (
          <AuditPackPreview
            controlType={panelData.controlType || 'Kontrolle'}
            groups={panelData.auditPackGroups}
            isComplete={auditPackComplete}
            onGenerate={() => console.log('Generate audit pack')}
            onDocumentClick={handleDocumentClick}
          />
        )}
      </ContextPanel>

      {/* Document Preview Panel (placeholder) */}
      <ContextPanel
        isOpen={panelType === 'document'}
        onClose={closePanel}
        title="Dokument"
        subtitle={panelData.documentId}
        width="wide"
      >
        <div
          style={{
            height: '400px',
            background: '#f3f4f6',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b7280',
          }}
        >
          {labels.documentPreview}: {panelData.documentId}
        </div>
      </ContextPanel>

      {/* Calculation Details Panel */}
      <CalculationDetailsPanel
        isOpen={panelType === 'calculation'}
        onClose={closePanel}
        details={panelData.calculationDetails || null}
        onEntityClick={handleEntityClick}
        onDocumentClick={handleDocumentClick}
      />
    </>
  );
};

export default WorkspacePage;
